<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * A SIXTH tone, and no arbitrary colour. A choice field carries a colour per
 * option, and the temptation is to let it through as an inline style — but the
 * palette flips between the light and the dark theme and an authored hex does
 * not, so a literal `#facc15` is a badge nobody can read on one of the two. The
 * colour is mapped onto these tones instead (`optionTone`, in the dynamic
 * resolvers); `accent` exists because five tones made two different statuses the
 * same grey, which is what having a colour was supposed to prevent.
 */
const badge = cva("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium", {
	variants: {
		tone: {
			neutral: "bg-bg-muted text-fg-muted",
			success: "bg-success-subtle text-success",
			warning: "bg-warning-subtle text-warning",
			danger: "bg-danger-subtle text-danger",
			info: "bg-info-subtle text-info",
			accent: "bg-accent-subtle text-accent",
		},
	},
	defaultVariants: { tone: "neutral" },
});

type Props = { tone?: NonNullable<VariantProps<typeof badge>["tone"]>; class?: string };
const props = defineProps<Props>();
const classes = computed(() => twMerge(badge({ tone: props.tone }), props.class));
</script>

<template>
	<span :class="classes"><slot /></span>
</template>
